import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  durationToneClass,
  failureToneClass,
  formatMs,
  formatPct,
} from '@/features/admin/processing-format'
import type { ProcessingPipelineState } from '@/services/api/adminProcessing'
import { StepsMobileList } from './StepsMobileList'

export function StepsCard({
  pipeline,
  rangeLabel,
}: {
  pipeline: ProcessingPipelineState
  rangeLabel: string
}) {
  const { t } = useTranslation()
  const m = pipeline.metrics
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t('admin.processing.steps.title')} · {rangeLabel}
        </CardTitle>
        <CardDescription>{t('admin.processing.steps.description')}</CardDescription>
      </CardHeader>
      <CardContent className="px-4 pb-2">
        {m ? (
          <Table className="max-md:hidden">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[240px]">{t('admin.processing.cols.step')}</TableHead>
                <TableHead className="text-right">{t('admin.processing.steps.colCount')}</TableHead>
                <TableHead className="text-right">{t('admin.processing.steps.colAvg')}</TableHead>
                <TableHead className="text-right">p50</TableHead>
                <TableHead className="text-right">p95</TableHead>
                <TableHead className="text-right">{t('admin.processing.steps.colFail')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {m.steps.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="p-4 text-sm text-muted-foreground">
                    {t('admin.processing.steps.empty')}
                  </TableCell>
                </TableRow>
              ) : (
                m.steps.map((s, i) => (
                  <TableRow key={s.name} className={i % 2 === 1 ? 'bg-muted/20' : ''}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {s.count}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums ${durationToneClass(s.avg_ms)}`}>
                      {formatMs(s.avg_ms)}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums ${durationToneClass(s.p50_ms)}`}>
                      {formatMs(s.p50_ms)}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums ${durationToneClass(s.p95_ms)}`}>
                      {formatMs(s.p95_ms)}
                    </TableCell>
                    <TableCell
                      className={`text-right tabular-nums ${failureToneClass(s.failure_rate)}`}
                    >
                      {formatPct(s.failure_rate)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        ) : (
          <div className="p-4 text-sm text-muted-foreground">—</div>
        )}
        {m && <StepsMobileList steps={m.steps} />}
      </CardContent>
    </Card>
  )
}
